import { Button, Center, Container, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Department, Team, UserResponse } from '@/api/generated/models';
import {
  getGetTeamQueryKey,
  getListMembersQueryKey,
  useGetDepartment,
  useGetTeam,
  useListMembers,
  useRemoveMember
} from '@/api/generated/departments-teams/departments-teams';
import { getListUsersQueryKey } from '@/api/generated/users/users';
import { BackButton, ConfirmActionModal, SearchField, tableRowStyle } from '@/components/common';
import { AddMemberModal } from '@/features/departments/components/AddMemberModal';
import { successNotificationIcon } from '@/theme/notificationIcons';

export const TeamDetailPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { departmentId, teamId } = useParams<{ departmentId: string; teamId: string }>();
  const deptId = departmentId ?? '';
  const id = teamId ?? '';

  const [search, setSearch] = useState('');
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [removingMember, setRemovingMember] = useState<UserResponse | undefined>(undefined);

  const { data: teamData, isLoading: teamLoading } = useGetTeam(deptId, id, {
    query: { enabled: !!deptId && !!id }
  });
  // GET .../teams/{teamId} vraća ResponseEntity<?> — isti springdoc obrazac
  // kao GET /departments/{id}, vidi DepartmentDetailPage.
  const team = teamData as Team | undefined;

  const { data: departmentData } = useGetDepartment(deptId, { query: { enabled: !!deptId } });
  const department = departmentData as Department | undefined;

  const { data: membersData, isLoading: membersLoading } = useListMembers(deptId, id, {
    query: { enabled: !!deptId && !!id }
  });
  const members: UserResponse[] = membersData ?? [];

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => {
      const fullName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.toLowerCase();
      return fullName.includes(query) || member.email?.toLowerCase().includes(query);
    });
  }, [members, search]);

  const roleLabel = (role?: string) =>
    role === 'organisation_admin'
      ? t('departments.team.roleAdmin')
      : t('departments.team.roleMember');

  const removeMutation = useRemoveMember({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(deptId, id) });
        queryClient.invalidateQueries({ queryKey: getGetTeamQueryKey(deptId, id) });
        // team_id promjena mijenja i broj članova prikazan na listi/detalju
        // odjela (računa se iz useListUsers()) — bez ovoga ta stranica i dalje
        // koristi stari cache i AddMemberModal ne vidi da je korisnik oslobođen.
        queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('departments.team.removeMemberSuccess')
        });
        setRemovingMember(undefined);
      }
    }
  });

  if (teamLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!team) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('departments.team.detailNotAvailable')}
        </Text>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={`/users/departments/${deptId}`} />

        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={4}>
            <Text fz={24} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {team.name}
            </Text>
            <Text fz={16} style={{ color: 'var(--color-text-secondary)' }}>
              {t('departments.team.subtitle', {
                department: department?.name ?? '—',
                count: members.length
              })}
            </Text>
            {team.description && (
              <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
                {team.description}
              </Text>
            )}
          </Stack>
          <Button variant="outline" onClick={() => setAddModalOpened(true)}>
            + {t('departments.team.addMember')}
          </Button>
        </Group>

        <div style={{ width: 320 }}>
          <SearchField
            hideLabel
            placeholder={t('departments.team.searchMembersPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </div>

        <Stack gap={0}>
          {!membersLoading && filteredMembers.length === 0 && (
            <Center py={32}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {members.length === 0
                  ? t('departments.team.noMembers')
                  : t('departments.team.noSearchResults')}
              </Text>
            </Center>
          )}

          {filteredMembers.length > 0 && (
            <Stack
              gap={0}
              style={{
                border: '1px solid var(--color-border-default)',
                borderRadius: 12,
                backgroundColor: 'var(--color-bg-surface)',
                overflow: 'hidden'
              }}
            >
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                <Text
                  fz={10}
                  fw={600}
                  w={280}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.team.colMemberName')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={220}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.team.colRole')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  w={100}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('departments.colAction')}
                </Text>
              </div>

              {filteredMembers.map((member, index) => (
                <div
                  key={member.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Stack gap={2} w={280}>
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {member.firstName} {member.lastName}
                    </Text>
                    <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                      {member.email}
                    </Text>
                  </Stack>
                  <Text fz={13} w={220} style={{ color: 'var(--color-text-secondary)' }}>
                    {roleLabel(member.role)}
                  </Text>
                  <Text
                    fz={12}
                    fw={600}
                    w={100}
                    ta="right"
                    onClick={() => setRemovingMember(member)}
                    style={{
                      cursor: 'pointer',
                      color: 'var(--color-status-danger)',
                      textDecoration: 'underline'
                    }}
                  >
                    {t('departments.team.removeMember')}
                  </Text>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      <AddMemberModal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        departmentId={deptId}
        teamId={id}
      />

      <ConfirmActionModal
        opened={!!removingMember}
        onClose={() => setRemovingMember(undefined)}
        onConfirm={() =>
          removingMember?.id &&
          removeMutation.mutate({ id: deptId, teamId: id, userId: removingMember.id })
        }
        loading={removeMutation.isPending}
        isPaused={removeMutation.isPaused}
        danger
        title={t('departments.team.removeMemberTitle')}
        message={t('departments.team.removeMemberMessage', {
          name: `${removingMember?.firstName ?? ''} ${removingMember?.lastName ?? ''}`.trim()
        })}
        confirmLabel={t('departments.team.removeMemberConfirm')}
      />
    </Container>
  );
};
